// ── Employee Performance: a receipt's items ─────────────────────────────
//
// The lines on one receipt in the drill-down, for the drill-down's own view: the widget's period and
// filters, the employee, and the receipt. Nothing is asked for until the receipt is opened — the
// drill-down mounts the row that calls this only then. A receipt's lines don't change once it is
// rung, so they are kept rather than asked for again when it is closed and reopened. Keyed under the
// page's main dashboard query, like the receipts, so the page's Refresh reloads an open receipt's
// items, and a closed one's the next time it is opened.

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useAuthStore } from '../../stores/authStore'
import type { EmployeePerformanceQueryContext } from '../../lib/employeePerformance'
import {
    buildEmployeeTransactionItemsUrl,
    fetchEmployeeTransactionItems,
    type EmployeeTransactionItemsData,
} from '../../lib/employeeTransactions'

/**
 * @param enabled False holds the request back, for a caller mounted before its receipt is opened. The
 * drill-down's detail row exists only while its receipt is open, so it leaves this as it is.
 */
export function useEmployeeTransactionItems(
    query: EmployeePerformanceQueryContext, employeeId: string, transactionId: string, enabled = true,
): UseQueryResult<EmployeeTransactionItemsData, Error> {
    const token = useAuthStore(s => s.accessToken)
    const url = buildEmployeeTransactionItemsUrl(query.period, query.filters, employeeId, transactionId)
    return useQuery<EmployeeTransactionItemsData, Error>({
        queryKey: [query.queryKeyPrefix, 'employee-performance', 'transactions', 'items', url],
        queryFn: () => fetchEmployeeTransactionItems(url, token),
        enabled,
        // Fresh until the page's Refresh invalidates it, whatever the app's default: reopening a
        // receipt shows what it showed before, and never asks again.
        staleTime: Infinity,
        // Asked once, whatever the app's default: a receipt that isn't in this view, or a refusal,
        // won't change on a second try, and the sentence saying so shows at once rather than after a
        // retry's wait. Closing the receipt and opening it again asks again.
        retry: false,
    })
}
